import React, { useMemo } from 'react';

import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import type { CharterReferralRow, CreateCharterReferralBody } from '~/types/charterReferral';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const luxuryEscapesEmailRegex = /^[^\s@]+@luxuryescapes\.com$/i;

function isValidEmail(value: string): boolean {
  return emailRegex.test(value.trim());
}

function isLuxuryEscapesEmail(value: string): boolean {
  return isValidEmail(value) && luxuryEscapesEmailRegex.test(value.trim());
}

interface Props {
  open: boolean;
  loading?: boolean;
  values: CreateCharterReferralBody;
  title: string;
  submitLabel: string;
  isEditMode?: boolean;
  existingRows?: Array<CharterReferralRow>;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: keyof CreateCharterReferralBody, value: string) => void;
}

export default function CharterReferralDialog({
  open,
  loading,
  values,
  title,
  submitLabel,
  isEditMode = false,
  existingRows = [],
  onClose,
  onSubmit,
  onChange,
}: Props): React.JSX.Element {
  const isFnfEmailInvalid = values.fnfEmail.trim() !== '' && !isLuxuryEscapesEmail(values.fnfEmail);
  const isReferralEmailInvalid =
    !isEditMode && values.referralEmail.trim() !== '' && !isValidEmail(values.referralEmail);
  const isDuplicateReferralEmail = useMemo(() => {
    if (isEditMode || values.referralEmail.trim() === '') {
      return false;
    }

    return existingRows.some((row) => row.referralEmail.toLowerCase() === values.referralEmail.trim().toLowerCase());
  }, [isEditMode, existingRows, values.referralEmail]);
  const isSubmitDisabled =
    Object.values(values).some((value) => value.trim() === '') ||
    isFnfEmailInvalid ||
    isReferralEmailInvalid ||
    isDuplicateReferralEmail;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { p: 1.25 } } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Referee Name"
            value={values.fnfName}
            onChange={(event) => onChange('fnfName', event.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Referee Email"
            type="email"
            value={values.fnfEmail}
            onChange={(event) => onChange('fnfEmail', event.target.value)}
            error={isFnfEmailInvalid}
            helperText={isFnfEmailInvalid ? 'Enter a valid @luxuryescapes.com email address.' : undefined}
            fullWidth
            required
          />
          <TextField
            label="Referral Name"
            value={values.referralName}
            onChange={(event) => onChange('referralName', event.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Referral Email"
            type="email"
            value={values.referralEmail}
            onChange={(event) => onChange('referralEmail', event.target.value)}
            error={isReferralEmailInvalid || isDuplicateReferralEmail}
            fullWidth
            required
            disabled={isEditMode}
            helperText={
              isEditMode
                ? 'Referral email cannot be edited. Delete and recreate the referral to change it.'
                : isDuplicateReferralEmail
                ? 'A referral with this email already exists.'
                : isReferralEmailInvalid
                ? 'Enter a valid email address.'
                : undefined
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton variant="contained" onClick={onSubmit} loading={loading} disabled={isSubmitDisabled}>
          {submitLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
